import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { LocationSwitcher } from './LocationSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { User, LogOut, Bell, FileDown, Menu } from 'lucide-react';
import { ModeBadge } from './ModeBadge';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Header = ({ collapsed, onMobileMenu }) => {
  const { user, logout } = useAuth();

  return (
    <header
      className={cn(
        "fixed top-0 right-0 z-30 h-16 glassmorphism transition-all duration-300 flex items-center justify-between gap-2 px-3 sm:px-4 lg:px-6",
        // Mobile: full width. Desktop: leave room for the sidebar (mini or full).
        "left-0",
        collapsed ? "lg:left-[70px]" : "lg:left-[260px]"
      )}
      data-testid="header"
    >
      {/* Left cluster: mobile hamburger + live badge */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMobileMenu}
          className="lg:hidden h-9 w-9 text-slate-300 hover:text-white hover:bg-slate-800"
          data-testid="header-mobile-menu-btn"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <ModeBadge />
      </div>

      {/* Right Side */}
      <div className="flex items-center gap-1 sm:gap-3 min-w-0">
        {/* Location Switcher */}
        <LocationSwitcher />

        {/* Download sales overview (marketing PDF). Full label on desktop,
            icon-only on mobile so it stays reachable in the narrow header. */}
        <a
          href={`${API_URL}/api/marketing/sales-sheet`}
          target="_blank"
          rel="noreferrer"
          className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-2.5 py-1.5 rounded-md border border-slate-700 hover:border-blue-500/60 hover:bg-slate-800 transition-colors"
          data-testid="download-sales-sheet-link"
          title="Download the SyncPoint sales overview (PDF)"
          aria-label="Download sales sheet"
        >
          <FileDown className="w-3.5 h-3.5" />
          <span className="hidden xl:inline">Sales sheet</span>
        </a>

        {/* Notifications */}
        <Button variant="ghost" size="icon" className="relative text-slate-400 hover:text-white hover:bg-slate-800" data-testid="notifications-btn">
          <Bell className="w-5 h-5" />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-[10px] font-bold text-white flex items-center justify-center">
            3
          </span>
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 px-1.5 sm:px-3 text-slate-300 hover:text-white hover:bg-slate-800" data-testid="user-menu-btn">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                <User className="w-4 h-4 text-blue-400" />
              </div>
              <span className="hidden xl:inline font-medium text-sm">{user?.name || 'Admin'}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" collisionPadding={12} className="w-48 bg-slate-900 border-slate-800">
            <DropdownMenuItem className="text-slate-300 focus:text-white focus:bg-slate-800">
              <User className="w-4 h-4 mr-2" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-slate-800" />
            <DropdownMenuItem 
              onClick={logout}
              className="text-rose-400 focus:text-rose-300 focus:bg-slate-800"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
